/**
 * weblabsService — GraphQL access + SQL builders for the Weblabs dashboard.
 *
 * Reuses the generic raw-SQL `getWeblabView` path (weblab_athena_client.py
 * Lambda, under finn-asfm-read-access-lambda-role) via `fetchWeblabView` — the
 * same mechanism the Weblab Tracker plugin uses. Targets
 * `sandbox.finn_preprocessed_data_weblabs` (same `sandbox` Glue database the
 * Revenue Bridging dashboard reads).
 *
 * SCALE MODEL: the dashboard NEVER pulls raw rows. Every view is a small,
 * server-side AGGREGATE keyed on `day` (the table's partition column, so Athena
 * prunes partitions and results stay tiny regardless of how many weblabs or how
 * long the range):
 *   - chart:      one row per day (pos / neg / annualized split)
 *   - KPIs:       a handful of rows (net, annualized net, top +/- weblab)
 *   - breakdown:  fetched on click for one day/bucket (top-N weblabs)
 *   - drill-in:   fetched on demand for one weblab (its daily series)
 * ALL filters (date range, marketplace, activation state, weblab type, weblab
 * start/launch-date range) are pushed into a shared WHERE, so the numbers are
 * always complete — no client-side row cap, no truncation.
 */

import {
  fetchWeblabView,
  extractGraphQLError,
} from "../weblabTracker/weblabTrackerService";
import { annualizedFieldFor, type WeblabMetric } from "./weblabsChart";

export { fetchWeblabView, extractGraphQLError };

export const WEBLABS_DATABASE = "sandbox";
const WEBLABS_TABLE = "finn_preprocessed_data_weblabs";

/** True for a strict YYYY-MM-DD date string (injection guard for the WHERE). */
export function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** Escape a single-quoted SQL string literal (for categorical filter values). */
export function sqlLiteral(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/** Every server-side filter the dashboard supports. */
export interface WeblabsQueryFilters {
  startDate: string;
  endDate: string;
  /** Marketplace (country) — "" / undefined = All. */
  country?: string;
  /** activation_state exact match — "" / undefined = All. */
  activationState?: string;
  /** weblab_type exact match — "" / undefined = All. */
  weblabType?: string;
  /** Weblab Start/Launch date lower bound (YYYY-MM-DD). */
  runStart?: string;
  /** Weblab Start/Launch date upper bound (YYYY-MM-DD). */
  runEnd?: string;
}

/**
 * The weblab's run/launch date, matching the client's `runLaunchDate`:
 * weblab_start_date when the weblab is running, else weblab_launch_date.
 */
const RUN_LAUNCH_EXPR =
  "CASE WHEN activation_state = 'run' THEN weblab_start_date " +
  "ELSE weblab_launch_date END";

/** Build the shared WHERE clause from the filters (+ optional extra predicates). */
function buildWhere(f: WeblabsQueryFilters, extra: string[] = []): string {
  if (!isIsoDate(f.startDate) || !isIsoDate(f.endDate)) {
    throw new Error("Invalid date range (expected YYYY-MM-DD)");
  }
  const clauses: string[] = [`day BETWEEN '${f.startDate}' AND '${f.endDate}'`];
  if (f.country && f.country.trim() !== "") {
    clauses.push(`country = ${sqlLiteral(f.country)}`);
  }
  if (f.activationState && f.activationState.trim() !== "") {
    clauses.push(`activation_state = ${sqlLiteral(f.activationState)}`);
  }
  if (f.weblabType && f.weblabType.trim() !== "") {
    clauses.push(`weblab_type = ${sqlLiteral(f.weblabType)}`);
  }
  if (f.runStart && isIsoDate(f.runStart)) {
    clauses.push(`${RUN_LAUNCH_EXPR} >= '${f.runStart}'`);
  }
  if (f.runEnd && isIsoDate(f.runEnd)) {
    clauses.push(`${RUN_LAUNCH_EXPR} <= '${f.runEnd}'`);
  }
  clauses.push(...extra);
  return `WHERE ${clauses.join("\n      AND ")}`;
}

/**
 * Per-(day, weblab) inner aggregate shared by several queries: the day's impact
 * for the chosen metric and the weblab's annualized value (summed across the
 * marketplaces present, matching the pre-aggregate semantics).
 */
function dayWeblabInner(f: WeblabsQueryFilters, metric: WeblabMetric): string {
  const ann = annualizedFieldFor(metric);
  return `SELECT day, weblab,
        SUM(${metric}) AS wl_impact,
        SUM(${ann}) AS wl_ann
      FROM ${WEBLABS_TABLE}
      ${buildWhere(f)}
      GROUP BY day, weblab`;
}

/**
 * Chart series — one row per day: the sum of positive weblabs (pos), the sum of
 * negative (neg), and the same split for the annualized value (ann_pos /
 * ann_neg, used by the daily tooltip). net = pos + neg (computed client-side).
 */
export function buildChartQuery(
  f: WeblabsQueryFilters,
  metric: WeblabMetric
): string {
  return `
    SELECT day,
      SUM(CASE WHEN wl_impact > 0 THEN wl_impact ELSE 0 END) AS pos,
      SUM(CASE WHEN wl_impact < 0 THEN wl_impact ELSE 0 END) AS neg,
      SUM(CASE WHEN wl_ann > 0 THEN wl_ann ELSE 0 END) AS ann_pos,
      SUM(CASE WHEN wl_ann < 0 THEN wl_ann ELSE 0 END) AS ann_neg
    FROM (
      ${dayWeblabInner(f, metric)}
    )
    GROUP BY day
    ORDER BY day
  `.trim();
}

/** CTE (day_wl → per_wl) shared by the KPI summary and top-mover queries. */
function kpiCte(f: WeblabsQueryFilters, metric: WeblabMetric): string {
  return `WITH day_wl AS (
      ${dayWeblabInner(f, metric)}
    ),
    per_wl AS (
      SELECT weblab,
        SUM(wl_impact) AS impact_total,
        MAX(wl_ann) AS ann
      FROM day_wl
      GROUP BY weblab
    )`;
}

/** KPI scalars over the range: net impact and net annualized (each weblab once). */
export function buildKpiSummaryQuery(
  f: WeblabsQueryFilters,
  metric: WeblabMetric
): string {
  return `${kpiCte(f, metric)}
    SELECT
      COALESCE(SUM(impact_total), 0) AS net,
      COALESCE(SUM(ann), 0) AS ann_net
    FROM per_wl`.trim();
}

/**
 * Top positive and top negative weblab over the range (by summed impact), each
 * with its annualized value. Returns up to 2 rows: kind = 'pos' | 'neg'. A kind
 * is omitted when no weblab qualifies (e.g. no positive weblab in range).
 */
export function buildKpiTopQuery(
  f: WeblabsQueryFilters,
  metric: WeblabMetric
): string {
  return `${kpiCte(f, metric)}
    SELECT * FROM (
      SELECT 'pos' AS kind, weblab, impact_total, ann
      FROM per_wl WHERE impact_total > 0
      ORDER BY impact_total DESC LIMIT 1
    )
    UNION ALL
    SELECT * FROM (
      SELECT 'neg' AS kind, weblab, impact_total, ann
      FROM per_wl WHERE impact_total < 0
      ORDER BY impact_total ASC LIMIT 1
    )`.trim();
}

/** Columns allowed for the distinct-options queries (identifier allowlist). */
export type DistinctColumn = "country" | "activation_state" | "weblab_type";

/**
 * Distinct values of a categorical column over the date range — powers a filter
 * dropdown. Independent of the other categorical selections so every option
 * stays visible. Only a fixed allowlist of columns is accepted.
 */
export function buildDistinctQuery(
  startDate: string,
  endDate: string,
  column: DistinctColumn
): string {
  if (!isIsoDate(startDate) || !isIsoDate(endDate)) {
    throw new Error("Invalid date range (expected YYYY-MM-DD)");
  }
  return `
    SELECT DISTINCT ${column} AS v
    FROM ${WEBLABS_TABLE}
    WHERE day BETWEEN '${startDate}' AND '${endDate}'
      AND ${column} IS NOT NULL
    ORDER BY v
  `.trim();
}

/** Fetch distinct values of an allow-listed column for the range. */
export async function fetchDistinct(
  startDate: string,
  endDate: string,
  column: DistinctColumn
): Promise<string[]> {
  const result = await fetchWeblabView({
    query: buildDistinctQuery(startDate, endDate, column),
    database: WEBLABS_DATABASE,
    max_results: 5000,
  });
  return (result.rows ?? [])
    .map((r: any) => r.v)
    .filter((v: any): v is string => typeof v === "string" && v.trim() !== "");
}

/**
 * Per-weblab breakdown for one day (or bucket range) — the click-to-pin panel.
 * Top `limit` weblabs by ABSOLUTE impact, each with its annualized value, so
 * the slice captures the biggest movers in BOTH directions (a signed ORDER BY
 * here would return only winners and hide every loser). The caller re-sorts for
 * display (winners first) and computes an "Other" rollup from the bucket net
 * minus the shown values.
 */
export function buildBreakdownQuery(
  f: WeblabsQueryFilters,
  metric: WeblabMetric,
  bucketStart: string,
  bucketEnd: string,
  limit: number = 50
): string {
  const scoped: WeblabsQueryFilters = {
    ...f,
    startDate: bucketStart,
    endDate: bucketEnd,
  };
  return `
    SELECT weblab, value, ann FROM (
      SELECT weblab,
        SUM(wl_impact) AS value,
        MAX(wl_ann) AS ann
      FROM (
        ${dayWeblabInner(scoped, metric)}
      )
      GROUP BY weblab
    )
    ORDER BY ABS(value) DESC
    LIMIT ${Math.max(1, Math.floor(limit))}
  `.trim();
}

/**
 * Daily series for a single weblab (drill-in mini chart) plus its attributes
 * (type / owner / activation state / dates / annualized) carried on every row
 * via MAX so the caller can read them off any row.
 */
export function buildDrillQuery(
  f: WeblabsQueryFilters,
  metric: WeblabMetric,
  weblab: string
): string {
  const where = buildWhere(f, [`weblab = ${sqlLiteral(weblab)}`]);
  return `
    SELECT day,
      SUM(${metric}) AS value,
      MAX(weblab_type) AS weblab_type,
      MAX(primary_owner) AS primary_owner,
      MAX(activation_state) AS activation_state,
      MAX(weblab_start_date) AS weblab_start_date,
      MAX(weblab_launch_date) AS weblab_launch_date,
      MAX(sb_rev_annualized) AS sb_rev_annualized,
      MAX(sp_rev_annualized) AS sp_rev_annualized
    FROM ${WEBLABS_TABLE}
    ${where}
    GROUP BY day
    ORDER BY day
  `.trim();
}
